import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { trpcClient } from '../../../utils/trpc'
import type { LoopSearchOutput } from '../types'

// Types for the store
export type LoopListItem = Pick<LoopSearchOutput, 'name' | 'status' | 'id'> & {
	actions: string[]
	executionCount?: number
	lastExecutionDate?: string | null
	scheduleStartDate?: unknown
	scheduleEndDate?: unknown
}

export type LoopDashboardOrganization = {
	id: string
	name: string
	logo: string | null
}

export interface PaginationMeta {
	page: number
	perPage: number
	total: number
	totalInPage: number
}

export interface SearchParams {
	search?: string
	page?: number
	perPage?: number
	sortBy?: string
	order?: 'asc' | 'desc'
}

// Store interface
export interface LoopDashboardStore {
	// State from BFF
	loops: LoopListItem[]
	meta: PaginationMeta
	organization: LoopDashboardOrganization | null
	actions: string[]

	// UI State
	isLoading: boolean
	error: string | null

	// Actions
	initialize: (organizationId: string, params?: SearchParams) => Promise<void>
	setSearchQuery: (value?: string) => void
	searchQuery: string
	search: () => Promise<void>
	selectedLoop: LoopListItem | null
	setSelectedLoop: (loop: LoopListItem | null) => void
	duplicateSelectedLoop: () => Promise<string>
	deleteSelectedLoop: () => Promise<void>
	setError: (error?: string | null) => void
}

// Type for the store instance
export type LoopDashboardStoreInstance = ReturnType<typeof createLoopDashboardStore>

// Factory function for creating store instances
export const createLoopDashboardStore = () => {
	return create<LoopDashboardStore>()(
		devtools(
			(set, get) => ({
				// Initial state
				loops: [],
				organization: null,
				meta: { page: 1, perPage: 20, total: 0, totalInPage: 0 },
				actions: [],
				isLoading: false,
				error: null,
				selectedLoop: null,
				searchQuery: '',

				// Actions
				initialize: async (organizationId, params = {}) => {
					set({ isLoading: true, error: null })
					try {
						const data = await trpcClient.client.loops.search.query({
							organizationId,
							search: params.search,
							page: params.page,
							perPage: params.perPage,
							order: params.order,
							sortBy:
								params.sortBy === 'name' || params.sortBy === 'createdAt' || params.sortBy === 'updatedAt'
									? params.sortBy
									: undefined,
						})

						set({
							loops: data.data.map((loop) => ({
								id: loop.id,
								name: loop.name,
								status: loop.status,
								actions: ['duplicateLoop', 'deleteLoop'],
								executionCount: loop.executionCount,
								participantCount: loop.participantCount,
							})),
							meta: data.meta,
							actions: ['createLoop'],
							isLoading: false,
						})
					} catch (error) {
						console.error('Failed to load loops:', error)
						set({
							error: error instanceof Error ? error.message : 'Failed to load loop dashboard',
							isLoading: false,
						})
						return
					}

					try {
						const org = await trpcClient.client.organizations.getById.query({
							id: organizationId,
						})
						set({
							organization: org ? { id: org.id, name: org.name, logo: org.logo } : null,
						})
					} catch (error) {
						console.error('Failed to load organization info:', error)
						set({
							error: error instanceof Error ? error.message : 'Failed to load organization info',
						})
					}
				},

				search: async () => {
					set({ error: null })
					const { organization, searchQuery } = get()

					try {
						if (!organization) {
							throw new Error('No organization available')
						}

						const data = await trpcClient.client.loops.search.query({
							organizationId: organization.id,
							search: searchQuery,
							page: 1,
							perPage: 20,
							sortBy: 'createdAt',
							order: 'asc',
						})
						set({
							loops: data.data.map((loop) => ({
								id: loop.id,
								name: loop.name,
								status: loop.status,
								actions: ['duplicateLoop', 'deleteLoop'],
								executionCount: loop.executionCount,
								participantCount: loop.participantCount,
							})),
							meta: data.meta,
						})
					} catch (error) {
						console.error('Failed to search loops:', error)
						set({
							error: error instanceof Error ? error.message : 'Failed to search loops',
						})
					}
				},

				setSearchQuery: (value = '') => {
					set({ searchQuery: value })
				},

				duplicateSelectedLoop: async () => {
					const { selectedLoop, organization } = get()
					if (!selectedLoop || !organization) {
						throw new Error('No loop or organization selected')
					}

					try {
						const loopData = await trpcClient.client.loops.getById.query({
							id: selectedLoop.id,
							organizationId: organization.id,
						})

						if (!loopData) {
							throw new Error('Loop not found')
						}

						const created = await trpcClient.client.loops.create.mutate({
							organizationId: organization.id,
							name: `Copy of ${loopData.name}`,
							description: loopData.description ?? undefined,
							textQuestionLabel: loopData.textQuestionLabel ?? undefined,
							ratingQuestionLabel: loopData.ratingQuestionLabel ?? undefined,
							textQuestionEnabled: loopData.textQuestionEnabled,
							ratingQuestionEnabled: loopData.ratingQuestionEnabled,
							ratingScale: loopData.ratingScale as 5 | 10,
							isReusable: loopData.isReusable,
						})

						await get().search()
						return created.id
					} catch (error) {
						console.error('Failed to duplicate loop:', error)
						set({ error: error instanceof Error ? error.message : 'Failed to duplicate loop' })
						throw error
					}
				},

				deleteSelectedLoop: async () => {
					const { selectedLoop, organization } = get()
					if (!selectedLoop || !organization) {
						throw new Error('No loop or organization selected')
					}

					try {
						await trpcClient.client.loops.delete.mutate({
							id: selectedLoop.id,
							organizationId: organization.id,
						})
						await get().search()
					} catch (error) {
						console.error('Failed to delete loop:', error)
						set({ error: error instanceof Error ? error.message : 'Failed to delete loop' })
						throw error
					}
				},

				setSelectedLoop: (loop: LoopListItem | null) => {
					set({ selectedLoop: loop })
				},

				setError: (error = null) => {
					set({ error })
				},
			}),
			{ name: 'loop-dashboard-store' },
		),
	)
}
